/**
 * SC-02 — Insert generated text into the current slide (AIVA-6753).
 *
 * Adds ONE new text box to the selected slide and never touches existing
 * shapes, so a single Ctrl+Z in PowerPoint removes it (spec AC-2).
 *
 * Deliberately separate from `slideOps.insertTextOnSlide`: that helper is also
 * called by Deckorate's Get Insights flow, which is out of scope for the general
 * add-in, and it swallows every failure into `false`. This one differs in two
 * ways the action bar needs:
 *
 *  - Geometry comes from the real slide size (gap G-4). The prototype's fixed
 *    `50/240, 860×200` box assumed a 16:9 stage, so 4:3 and custom decks placed
 *    it badly. The same proportions are now applied to whatever size the deck
 *    reports, and the text auto-fits the box so a long reply cannot spill past
 *    the slide edge.
 *  - The box goes BELOW the content already on the slide ({@link placeBelowContent}).
 *    The spec's fixed position (44% down) only clears a title, so on a slide
 *    with body text the new box landed on top of it. See {@link chooseInsertPlacement}
 *    for slides with no room below.
 *  - Failures throw a named error ({@link NO_SLIDE_SELECTED_ERROR},
 *    {@link POWERPOINT_UNAVAILABLE_ERROR}) so the pill dispatcher can toast a
 *    message that says what to do, rather than a generic "Couldn't do that".
 */

/** Thrown when no slide is selected (e.g. Slide Sorter view with nothing picked). */
export const NO_SLIDE_SELECTED_ERROR = 'No slide selected';
/** Thrown when the pane is not running inside PowerPoint. */
export const POWERPOINT_UNAVAILABLE_ERROR = 'PowerPoint host unavailable';
/** Thrown when the text has nothing left to place once cleaned. */
export const NOTHING_TO_INSERT_ERROR = 'Nothing to insert';
/** Thrown when the slide is full and the fallback is 'refuse' (option C). */
export const SLIDE_FULL_ERROR = 'Slide is full';

/**
 * The stage the prototype's fixed geometry was designed on: 13.333in × 7.5in
 * (16:9) in points. Used as the fallback when the host cannot report the deck's
 * own size, which keeps the old placement exactly on those hosts.
 */
export const DEFAULT_SLIDE_SIZE = { width: 960, height: 540 } as const;

/** Font size for the inserted body text (spec SC-02 behaviour). */
export const INSERTED_TEXT_FONT_SIZE = 18;

export interface SlideSize {
  readonly width: number;
  readonly height: number;
}

export interface TextBoxGeometry {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the new text box goes, as a share of the slide.
 *
 * These are the prototype's `50/240, 860×200` on a 960×540 stage, turned into
 * proportions: the box starts 44% down, which clears a standard title
 * placeholder (T-SC02-2), and ends at 81%, inside the slide.
 */
export function textBoxGeometry(size: SlideSize): TextBoxGeometry {
  return {
    left: size.width * (50 / 960),
    top: size.height * (240 / 540),
    width: size.width * (860 / 960),
    height: size.height * (200 / 540),
  };
}

/** A shape already on the slide, in points. Without `left`/`width` it spans the slide width. */
export interface OccupiedBand {
  readonly top: number;
  readonly height: number;
  readonly left?: number;
  readonly width?: number;
}

/** Gap left between the existing content and the new box. */
export const CONTENT_GAP_RATIO = 0.02;
/** Bottom margin kept clear, so the box never touches the slide edge. */
export const BOTTOM_MARGIN_RATIO = 0.05;
/**
 * Below this much free height the slide counts as full. 12% of a 16:9 slide is
 * about 65pt — three lines of 18pt text before auto-fit starts shrinking it.
 */
export const MIN_BOX_HEIGHT_RATIO = 0.12;
/**
 * A shape at least this tall is treated as background (a full-bleed picture or
 * a frame), not content — counting it would leave no room on any such slide.
 */
export const BACKGROUND_HEIGHT_RATIO = 0.9;
/** A shape starting this far down is a footer (text, logo, slide number): the box ends above it. */
export const FOOTER_TOP_RATIO = 0.85;
/** Top share of the slide kept for the title; free space is only searched below it. */
export const TITLE_ZONE_RATIO = 0.2;
/** Narrowest free area worth using, as a share of the slide width. */
export const MIN_FREE_WIDTH_RATIO = 0.3;
/** The free-space search is O(n⁴); slides with more shapes are treated as full. */
const MAX_SHAPES_FOR_FREE_SPACE = 60;
/** Box placed beside a full slide: gap from the slide edge and width, as shares of the slide width. */
const BESIDE_GAP_RATIO = 0.03;
const BESIDE_WIDTH_RATIO = 0.4;

interface Rect {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/**
 * The box under the lowest content on the slide, or null when less than
 * {@link MIN_BOX_HEIGHT_RATIO} is free there.
 *
 * An empty slide, or content ending above the spec position (just a title),
 * gets the spec position (T-SC02-2). Footers act as the floor, not content.
 */
export function placeBelowContent(
  size: SlideSize,
  occupied: readonly OccupiedBand[]
): TextBoxGeometry | null {
  const gap = size.height * CONTENT_GAP_RATIO;
  const visible = visibleShapes(size, occupied);
  const isFooter = (s: OccupiedBand) => s.top >= size.height * FOOTER_TOP_RATIO;
  const content = visible.filter((s) => !isFooter(s));
  const bottom = contentFloor(size, visible);
  const spec = textBoxGeometry(size);
  const specFits = { ...spec, height: Math.min(spec.height, bottom - spec.top) };
  if (content.length === 0) return specFits;

  const lowest = Math.min(Math.max(...content.map((s) => s.top + s.height)), size.height);
  const top = lowest + gap;
  if (top <= spec.top) return specFits;

  if (bottom - top < size.height * MIN_BOX_HEIGHT_RATIO) return null;
  return { left: spec.left, top, width: spec.width, height: bottom - top };
}

/** Shapes shown on the slide: not background, and not below or beside it. */
function visibleShapes(size: SlideSize, occupied: readonly OccupiedBand[]): OccupiedBand[] {
  return occupied.filter(
    (s) =>
      s.height > 0 &&
      s.height < size.height * BACKGROUND_HEIGHT_RATIO &&
      s.top < size.height &&
      !isBesideSlide(size, s)
  );
}

function isBesideSlide(size: SlideSize, s: OccupiedBand): boolean {
  const left = s.left ?? 0;
  return left >= size.width || left + (s.width ?? size.width) <= 0;
}

/** Lowest point a box may reach: the bottom margin, or just above a footer. */
function contentFloor(size: SlideSize, visible: readonly OccupiedBand[]): number {
  const gap = size.height * CONTENT_GAP_RATIO;
  return Math.min(
    size.height * (1 - BOTTOM_MARGIN_RATIO),
    ...visible.filter((s) => s.top >= size.height * FOOTER_TOP_RATIO).map((s) => s.top - gap)
  );
}

/**
 * The largest free area on the slide, or null when none is at least
 * {@link MIN_FREE_WIDTH_RATIO} wide and {@link MIN_BOX_HEIGHT_RATIO} tall.
 * Searched between the title zone and the footer floor, keeping a gap round every shape.
 */
export function findFreeSpace(size: SlideSize, occupied: readonly OccupiedBand[]): TextBoxGeometry | null {
  const visible = visibleShapes(size, occupied);
  if (visible.length > MAX_SHAPES_FOR_FREE_SPACE) return null;
  const gap = size.height * CONTENT_GAP_RATIO;
  const minLeft = textBoxGeometry(size).left;
  const maxRight = size.width - minLeft;
  const minTop = size.height * TITLE_ZONE_RATIO;
  const floor = contentFloor(size, visible);

  const obstacles: Rect[] = visible.map((s) => {
    const left = s.left ?? 0;
    return {
      left: left - gap,
      right: left + (s.width ?? size.width) + gap,
      top: s.top - gap,
      bottom: s.top + s.height + gap,
    };
  });
  const lefts = [minLeft, ...obstacles.map((o) => o.right)].filter((x) => x >= minLeft && x < maxRight);
  const rights = [maxRight, ...obstacles.map((o) => o.left)].filter((x) => x > minLeft && x <= maxRight);
  const tops = [minTop, ...obstacles.map((o) => o.bottom)].filter((y) => y >= minTop && y < floor);

  const candidates = tops.flatMap((top) =>
    lefts.flatMap((left) => rights.map((right) => freeRect(obstacles, { left, right, top, bottom: floor })))
  );
  const fits = candidates.filter(
    (r): r is Rect =>
      r !== null &&
      r.right - r.left >= size.width * MIN_FREE_WIDTH_RATIO &&
      r.bottom - r.top >= size.height * MIN_BOX_HEIGHT_RATIO
  );
  const [first, ...rest] = fits;
  if (!first) return null;
  const best = rest.reduce((a, b) => (area(b) > area(a) ? b : a), first);
  return { left: best.left, top: best.top, width: best.right - best.left, height: best.bottom - best.top };
}

function area(r: Rect): number {
  return (r.right - r.left) * (r.bottom - r.top);
}

/** `span` cut short at the first shape below its top; null when a shape covers its top. */
function freeRect(obstacles: readonly Rect[], span: Rect): Rect | null {
  let bottom = span.bottom;
  for (const o of obstacles) {
    if (o.left >= span.right || o.right <= span.left) continue;
    if (o.top <= span.top && o.bottom > span.top) return null;
    if (o.top > span.top) bottom = Math.min(bottom, o.top);
  }
  return { ...span, bottom };
}

/**
 * A box on the pasteboard right of the slide (option B). It shows in Normal
 * view, not in the slideshow; repeated inserts stack down the column.
 */
export function placeBesideSlide(size: SlideSize, occupied: readonly OccupiedBand[]): TextBoxGeometry {
  const gap = size.height * CONTENT_GAP_RATIO;
  const spec = textBoxGeometry(size);
  const firstTop = size.height * BOTTOM_MARGIN_RATIO;
  const besideBottoms = occupied
    .filter((s) => (s.left ?? 0) >= size.width)
    .map((s) => s.top + s.height + gap);
  const next = Math.max(firstTop, ...besideBottoms);
  const top = next + spec.height <= size.height ? next : firstTop;
  return {
    left: size.width * (1 + BESIDE_GAP_RATIO),
    top,
    width: size.width * BESIDE_WIDTH_RATIO,
    height: spec.height,
  };
}

/** Full-slide fallback: B places the box beside the slide, C refuses. */
export type FullSlideFallback = 'beside' | 'refuse';

/**
 * Temporary switch for comparing B and C; remove once product picks one.
 * `localStorage.setItem('aiva.ppt.fullSlideFallback', 'refuse')` selects C.
 */
export const FULL_SLIDE_FALLBACK_KEY = 'aiva.ppt.fullSlideFallback';

function fullSlideFallback(): FullSlideFallback {
  try {
    return globalThis.localStorage?.getItem(FULL_SLIDE_FALLBACK_KEY) === 'refuse' ? 'refuse' : 'beside';
  } catch {
    return 'beside';
  }
}

/** Replaces "Inserted into slide" when the box went beside the slide. */
export const PLACED_BESIDE_SLIDE_TOAST = 'This slide is full, so the text is beside it. Drag it into place.';

/**
 * Clean an AiVA reply for a slide (spec §4.1 / SC-02 AC-4).
 *
 * PowerPoint shows Markdown markers literally, so `**Q3**` would land on the
 * slide with its asterisks. The response contract already asks the model for
 * plain text; this is the half that does not depend on the model obeying it.
 * Bullet markers are dropped too: each line is already its own paragraph, and
 * a leading "- " reads as a stray dash on a slide.
 */
export function toSlideText(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)\*(?!\w)/g, '$1$2')
    .replace(/`([^`\n]*)`/g, '$1')
    .split('\n')
    .map((line) =>
      line
        .replace(/^\s{0,3}#{1,6}\s+/, '')
        .replace(/^\s*[-*•]\s+/, '')
        .trimEnd()
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

interface PageSetupLike {
  slideWidth?: number;
  slideHeight?: number;
  load: (props: string) => void;
}

/**
 * The deck's slide size in points, or {@link DEFAULT_SLIDE_SIZE} when the host
 * cannot say.
 *
 * `presentation.pageSetup` is newer than the typings this project builds
 * against, so it is read defensively: older hosts simply do not have it. Read in
 * its own sync so a host that rejects it cannot take the insert down with it.
 * Also sizes SC-05's new slide (addAsNewSlide.ts).
 */
export async function readSlideSize(context: PowerPoint.RequestContext): Promise<SlideSize> {
  const pageSetup = (context.presentation as unknown as { pageSetup?: PageSetupLike }).pageSetup;
  if (!pageSetup || typeof pageSetup.load !== 'function') return DEFAULT_SLIDE_SIZE;
  try {
    pageSetup.load('slideWidth,slideHeight');
    await context.sync();
    const { slideWidth, slideHeight } = pageSetup;
    if (
      typeof slideWidth === 'number' &&
      slideWidth > 0 &&
      typeof slideHeight === 'number' &&
      slideHeight > 0
    ) {
      return { width: slideWidth, height: slideHeight };
    }
  } catch {
    /* page setup not supported on this host — fall back below */
  }
  return DEFAULT_SLIDE_SIZE;
}

/**
 * Shape types that show nothing once their text is gone. A placeholder shows
 * only "Click to add text"; a text box shows nothing at all. Anything else — a
 * rectangle, a picture — is visible content with or without text.
 */
const TEXT_ONLY_SHAPE_TYPES: ReadonlySet<string> = new Set(['placeholder', 'textbox']);

/**
 * The vertical extent of every shape that shows content on the slide.
 *
 * Empty placeholders and empty text boxes are skipped. A Title + Content
 * layout's unused body box covers most of the slide but shows only "Click to
 * add text", and a text box whose text was deleted shows nothing — counting
 * either would make the slide look full. The reported case was the second: text
 * boxes left behind by earlier inserts, emptied but not deleted, reached past
 * the bottom edge, so every new insert fell back to mid-slide, over the body
 * text. Whether a shape has text is read in its own sync, because shapes without
 * a text frame (pictures, charts) cannot answer — if that read fails, the shapes
 * are kept, which only ever moves the box lower, never onto content.
 *
 * Returns [] when the shapes cannot be read at all; the caller then uses the
 * spec position, exactly as before this read existed.
 */
async function readOccupiedBands(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide
): Promise<OccupiedBand[]> {
  try {
    const shapes = slide.shapes;
    shapes.load('items/type,items/left,items/top,items/width,items/height');
    await context.sync();
    const textOnly = shapes.items.filter((s) => TEXT_ONLY_SHAPE_TYPES.has(String(s.type).toLowerCase()));
    const empty = new Set<PowerPoint.Shape>();
    if (textOnly.length > 0) {
      try {
        for (const s of textOnly) s.textFrame.load('hasText');
        await context.sync();
        for (const s of textOnly) if (!s.textFrame.hasText) empty.add(s);
      } catch {
        /* text frame unreadable — keep every such shape as content */
      }
    }
    return shapes.items
      .filter((s) => !empty.has(s))
      .map((s) => ({ top: s.top, height: s.height, left: s.left, width: s.width }));
  } catch {
    return [];
  }
}

/**
 * Text formatting is best-effort: a host that refuses autosize or font size
 * still gets the text, which is the part the user asked for.
 */
function formatInsertedBox(shape: PowerPoint.Shape, asBullets: boolean, fontSize: number): void {
  try {
    shape.textFrame.wordWrap = true;
    shape.textFrame.autoSizeSetting = 'AutoSizeTextToFitShape';
  } catch {
    /* autosize unavailable on this host */
  }
  try {
    shape.textFrame.textRange.font.size = fontSize;
  } catch {
    /* font API unavailable on this host */
  }
  if (asBullets) {
    try {
      shape.textFrame.textRange.paragraphFormat.bulletFormat.visible = true;
    } catch {
      /* bullet API unavailable on this host — the lines still go in, unbulleted */
    }
  }
}

/**
 * Whether the text should go in as a bulleted list (AIVA-6809).
 *
 * The response contract writes slide content one bullet per line with no
 * Markdown, and {@link toSlideText} strips any "- " markers that slip through —
 * so without real PowerPoint bullets a list lands as bare lines. Two or more
 * lines is a list; a single line (a title, a headline) stays plain.
 */
export function isBulletList(slideText: string): boolean {
  return slideText.split('\n').filter((line) => line.trim()).length >= 2;
}

/** Drops blank lines, which would show as empty bullets. */
export function withoutBlankLines(slideText: string): string {
  return slideText
    .split('\n')
    .filter((line) => line.trim())
    .join('\n');
}

/** Smallest font {@link fitFontSize} will shrink the text to. */
export const MIN_INSERTED_TEXT_FONT_SIZE = 10;
/** Average Latin glyph width as a share of the font size (Calibri/Arial ≈ 0.5). */
const CHAR_WIDTH_RATIO = 0.52;
const LINE_HEIGHT_RATIO = 1.2;
/** PowerPoint's default text box insets (0.1in left/right, 0.05in top/bottom), in points. */
const INSET_X = 14.4;
const INSET_Y = 7.2;
/** Room PowerPoint takes for a bullet and its hanging indent, in points. */
const BULLET_INDENT = 18;

/**
 * Largest font size (18 down to 10) at which the text fits the box, from an
 * estimate of how the lines wrap. Needed because Office.js auto-fit only
 * applies once the user edits the box.
 */
export function fitFontSize(slideText: string, box: TextBoxGeometry, asBullets: boolean): number {
  const lineWidth = box.width - INSET_X - (asBullets ? BULLET_INDENT : 0);
  const paragraphs = slideText.split('\n');
  for (let size = INSERTED_TEXT_FONT_SIZE; size > MIN_INSERTED_TEXT_FONT_SIZE; size--) {
    const charsPerLine = Math.max(1, Math.floor(lineWidth / (size * CHAR_WIDTH_RATIO)));
    const lines = paragraphs.reduce((sum, p) => sum + Math.max(1, Math.ceil(p.length / charsPerLine)), 0);
    if (lines * size * LINE_HEIGHT_RATIO + INSET_Y <= box.height) return size;
  }
  return MIN_INSERTED_TEXT_FONT_SIZE;
}

export interface InsertIntoSlideResult {
  readonly inserted: true;
  /** Id of the new shape, when the host reports it. */
  readonly shapeId?: string;
  /** Replaces the default success toast. */
  readonly toast?: string;
}

/**
 * Where the new box goes: below the content, else the largest free area (A).
 * On a full slide, B places it beside the slide and C throws {@link SLIDE_FULL_ERROR}.
 */
export function chooseInsertPlacement(
  size: SlideSize,
  occupied: readonly OccupiedBand[],
  fallback: FullSlideFallback
): { box: TextBoxGeometry; beside: boolean } {
  const onSlide = placeBelowContent(size, occupied) ?? findFreeSpace(size, occupied);
  if (onSlide) return { box: onSlide, beside: false };
  if (fallback === 'refuse') throw new Error(SLIDE_FULL_ERROR);
  return { box: placeBesideSlide(size, occupied), beside: true };
}

/**
 * Add `text` to the selected slide as a new text box.
 *
 * Throws {@link NO_SLIDE_SELECTED_ERROR}, {@link POWERPOINT_UNAVAILABLE_ERROR},
 * {@link NOTHING_TO_INSERT_ERROR} or {@link SLIDE_FULL_ERROR}; any other error
 * is a host failure.
 */
export async function insertTextIntoCurrentSlide(text: string): Promise<InsertIntoSlideResult> {
  if (!hasPpt()) throw new Error(POWERPOINT_UNAVAILABLE_ERROR);
  const slideText = toSlideText(text);
  if (!slideText) throw new Error(NOTHING_TO_INSERT_ERROR);

  return await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load('items/id');
    await context.sync();
    const slide = selected.items[0];
    if (!slide) throw new Error(NO_SLIDE_SELECTED_ERROR);

    const asBullets = isBulletList(slideText);
    const boxText = asBullets ? withoutBlankLines(slideText) : slideText;
    const size = await readSlideSize(context);
    const occupied = await readOccupiedBands(context, slide);
    const { box, beside } = chooseInsertPlacement(size, occupied, fullSlideFallback());
    const shape = slide.shapes.addTextBox(boxText, box);
    formatInsertedBox(shape, asBullets, fitFontSize(boxText, box, asBullets));
    shape.load('id');
    await context.sync();
    return {
      inserted: true as const,
      ...(shape.id ? { shapeId: shape.id } : {}),
      ...(beside ? { toast: PLACED_BESIDE_SLIDE_TOAST } : {}),
    };
  });
}
